import { Progress } from "@/components/ui/Progress";
import { cn } from "@/lib/utils/cn";
import { goalDisplay, formatGoalProgress } from "@/lib/utils/goalDisplay";
import type { Goal } from "@/lib/schemas";

/**
 * Re-usable inline progres dla aktywnego celu — używany w Dashboard hero,
 * Wallet hero chip, Chores task hints. Pokazuje:
 * - emoji + tytuł celu
 * - progress bar do target
 * - X / Y (jednostka zależy od kind: zł lub liczba zadań)
 *
 * `compact` (default false): krótszy layout, bez progress bar — do chipów
 * pod nagłówkami / przy task buttonach.
 *
 * `progress` to aktualna wartość:
 * - individual_money → `balanceMinor` (z member.balanceMinor)
 * - family_* → liczba approved completion (z `useGoalProgress`)
 */
export function GoalProgressInline({
  goal,
  progress,
  compact = false,
  tone = "purple",
  className,
}: {
  goal: Goal;
  progress: number;
  compact?: boolean;
  tone?: "purple" | "white";
  className?: string;
}) {
  const meta = goalDisplay(goal);
  const target =
    goal.kind === "individual_money" ? goal.targetMinor : goal.targetCount;
  const pct = Math.min(100, Math.round((progress / target) * 100));
  const fmt = formatGoalProgress(goal, progress);
  const isWhite = tone === "white";

  if (compact) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 text-[12px] font-semibold",
          isWhite ? "text-white/90" : "text-navy",
          className,
        )}
      >
        <span aria-hidden>{goal.emoji ?? meta.emoji}</span>
        <span className="truncate max-w-[160px]">{goal.title}</span>
        <span className={cn("font-num", isWhite ? "text-white" : "text-money")}>
          {pct}%
        </span>
      </span>
    );
  }

  return (
    <div className={className}>
      <div
        className={cn(
          "flex items-center gap-2 text-[13px] font-bold",
          isWhite ? "text-white" : "text-navy",
        )}
      >
        <span className="text-[18px]" aria-hidden>
          {goal.emoji ?? meta.emoji}
        </span>
        <span className="truncate flex-1">{goal.title}</span>
        <span
          className={cn(
            "font-num text-[12px] shrink-0",
            isWhite ? "text-white/85" : "text-slate",
          )}
        >
          {fmt.current} / {fmt.target}
        </span>
      </div>
      <Progress
        value={pct}
        height={6}
        tone={isWhite ? "white" : "money"}
        trackClassName={isWhite ? "bg-white/25" : undefined}
        className="mt-1.5"
      />
      <div
        className={cn(
          "mt-1 text-[11px] flex justify-between",
          isWhite ? "text-white/80" : "text-slate",
        )}
      >
        <span className="font-num">{pct}%</span>
        <span>
          {pct >= 100 ? "Cel zdobyty! 🎉" : `Brakuje ${fmt.remaining}`}
        </span>
      </div>
    </div>
  );
}
